import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from "@headlessui/react";
import { X } from "lucide-react";
import { mergePosition, Position } from "services/positions.service";
import PositionRoleChips from "./PositionRoleChips";
import {
  femaleNameMergeOption,
  filterPositions,
  genderedTitle,
  unionRoleRefs,
} from "./positions-helpers";
import { describePositionsError } from "./positions-errors";

/**
 * «Sujungti»: fold a duplicate position into another one. Holders move to the
 * target, the target keeps the union of both positions' pareigybės (which
 * applies to every holder at once, no approval — rule 11) and the source is
 * archived. The confirm step states that union so nobody is surprised by new
 * instructions landing on the target's holders.
 */

type Props = {
  companyId: string;
  source: Position;
  /** Candidates — active positions; the source itself is filtered out here. */
  positions: Position[];
  onClose: () => void;
  onMerged: () => void;
};

export default function PositionMergeDialog({ companyId, source, positions, onClose, onMerged }: Props) {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [targetId, setTargetId] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [merging, setMerging] = useState(false);
  // null = follow the default for the picked target (see femaleNameMergeOption).
  const [asFemaleChoice, setAsFemaleChoice] = useState<boolean | null>(null);

  const candidates = useMemo(
    () => filterPositions(positions.filter((p) => p.id !== source.id && !p.isArchived), query),
    [positions, source.id, query]
  );
  const target = positions.find((p) => p.id === targetId) || null;
  const unitedRoles = target ? unionRoleRefs(target.roles, source.roles) : [];
  const femaleOption = femaleNameMergeOption(source, target);
  const sourceAsFemaleName =
    femaleOption.available && (asFemaleChoice ?? femaleOption.defaultOn);

  const merge = async () => {
    if (!target) return;
    setMerging(true);
    try {
      const result = await mergePosition(companyId, source.id, target.id, { sourceAsFemaleName });
      const done = t("positions_merge_done", { source: source.nameLT, target: target.nameLT });
      toast.success(
        result.genderSetCount > 0
          ? `${done}. ${t("positions_merge_gender_set", { count: result.genderSetCount })}`
          : done
      );
      onMerged();
    } catch (e: any) {
      toast.error(describePositionsError(e, t));
      setMerging(false);
    }
  };

  return (
    <Dialog open onClose={merging ? () => {} : onClose} className="relative z-[1000]">
      <DialogBackdrop className="fixed inset-0 bg-zinc-950/50" />
      <div className="fixed inset-0 flex items-start justify-center p-2 sm:p-6">
        <DialogPanel className="mt-2 flex max-h-[85dvh] w-full max-w-xl flex-col overflow-hidden rounded-xl bg-white shadow-xl ring-1 ring-zinc-950/10 sm:mt-8">
          <div className="flex items-center gap-3 border-b border-gray-200 px-4 py-3">
            <DialogTitle className="min-w-0 flex-1 truncate text-sm font-semibold text-gray-900">
              {t("positions_merge_title", { name: source.nameLT })}
            </DialogTitle>
            <button
              type="button"
              onClick={onClose}
              disabled={merging}
              aria-label={t("close")}
              className="rounded-md p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-900 max-md:p-2.5"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {!confirming ? (
            <div className="flex-1 space-y-2 overflow-y-auto px-4 py-3">
              <p className="text-sm text-gray-600">{t("positions_merge_pick_hint")}</p>
              <input
                type="search"
                autoFocus
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={t("positions_picker_search")}
                aria-label={t("positions_picker_search")}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-base focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 md:text-sm"
              />
              <div className="max-h-72 divide-y divide-gray-100 overflow-y-auto rounded-md border border-gray-200">
                {candidates.length === 0 ? (
                  <div className="px-3 py-2 text-sm text-gray-500">{t("positions_picker_empty")}</div>
                ) : (
                  candidates.map((position) => (
                    <label
                      key={position.id}
                      className="flex cursor-pointer items-start gap-2 px-3 py-2 text-sm hover:bg-gray-50 max-md:py-3"
                    >
                      <input
                        type="radio"
                        name="merge-target"
                        checked={targetId === position.id}
                        onChange={() => {
                          setTargetId(position.id);
                          setAsFemaleChoice(null);
                        }}
                        className="mt-0.5 h-4 w-4 shrink-0 border-gray-300 text-blue-600 focus:ring-blue-500 max-md:h-5 max-md:w-5"
                      />
                      <span className="min-w-0">
                        <span className="block truncate text-gray-900">{genderedTitle(position)}</span>
                        <span className="block truncate text-xs text-gray-500">
                          {[position.nameEN, t("positions_holders_count", { count: position.holdersCount })]
                            .filter(Boolean)
                            .join(" · ")}
                        </span>
                      </span>
                    </label>
                  ))
                )}
              </div>
            </div>
          ) : (
            target && (
              <div className="flex-1 space-y-3 overflow-y-auto px-4 py-3 text-sm text-gray-800">
                <p>
                  {t("positions_merge_confirm_text", {
                    source: source.nameLT,
                    target: target.nameLT,
                    count: source.holdersCount,
                  })}
                </p>
                <div>
                  <p className="mb-1 font-medium">
                    {t("positions_merge_union_title", { target: target.nameLT })}
                  </p>
                  <PositionRoleChips roles={unitedRoles} emptyLabel={t("positions_no_roles")} />
                </div>
                {femaleOption.available && (
                  <label className="flex cursor-pointer items-start gap-2">
                    <input
                      type="checkbox"
                      checked={sourceAsFemaleName}
                      onChange={(event) => setAsFemaleChoice(event.target.checked)}
                      className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span>
                      {t("positions_merge_as_female", { source: source.nameLT, target: target.nameLT })}
                    </span>
                  </label>
                )}
                {unitedRoles.length > target.roles.length && (
                  <p className="rounded-md border border-amber-200 bg-amber-50 p-2 text-amber-900">
                    {t("positions_merge_union_warning", { target: target.nameLT })}
                  </p>
                )}
              </div>
            )
          )}

          <div className="flex justify-end gap-2 border-t border-gray-200 px-4 py-3">
            <button
              type="button"
              onClick={confirming ? () => setConfirming(false) : onClose}
              disabled={merging}
              className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 max-md:min-h-11"
            >
              {confirming ? t("back") : t("cancel")}
            </button>
            {!confirming ? (
              <button
                type="button"
                onClick={() => setConfirming(true)}
                disabled={!target}
                className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 max-md:min-h-11"
              >
                {t("positions_merge_next")}
              </button>
            ) : (
              <button
                type="button"
                onClick={merge}
                disabled={merging}
                className="rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50 max-md:min-h-11"
              >
                {t("positions_merge_action")}
              </button>
            )}
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}
